import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  NEW_PROJECT_ORGANISATION_TYPE_OPTIONS,
  OrganisationTypeConstants,
  ProjectAccessConstants,
} from "../../../../../constants";
import {
  AddedOrganisationAssignment,
  AddOrganisationSelection,
  NewProjectJourneyStep,
  ProjectRollupRow,
  SelectData,
} from "../../../../../models";
import { ServiceError } from "../../../../../service/Shared";
import { useOrganisationSearch } from "../../../../../utils";
import {
  NewProjectJourneyCorners,
  NewProjectJourneyVariant,
  useNewProjectJourneyContext,
} from "../NewProjectJourneyContext";
import {
  applyExistingDeveloperActions,
  buildResponsibleOwnerOptions,
  buildRollupRows,
  ExistingDeveloperPrompt,
  getActiveProjectRows,
  getLandholdingNameByRowKey,
  getProjectsNeedingExistingDeveloperAction,
  isPartiallyCreated as getIsPartiallyCreated,
  projectDisplayName,
  removeOrganisationFromProject,
  saveNewProjectJourney,
  syncCreatingOrganisation,
  validateAddSelection,
} from "../utils";

const emptySelection = (): AddOrganisationSelection => ({
  organisationType: null,
  organisationUuid: null,
  access: null,
  projectKeys: [],
  existingDeveloperActions: {},
});

export type ExistingDeveloperPromptOption = ExistingDeveloperPrompt & { projectName: string };

interface UseProjectAccessStepReturnData {
  variant: NewProjectJourneyVariant;
  corners: NewProjectJourneyCorners;
  organisationTypeOptions: SelectData;
  organisationOptions: SelectData;
  accessOptions: SelectData;
  projectOptions: SelectData;
  selection: AddOrganisationSelection;
  rollupRows: ProjectRollupRow[];
  rollupResetKey: number;
  responsibleOwnerOptions: (projectKey: string) => SelectData;
  existingDeveloperPrompts: ExistingDeveloperPromptOption[];
  existingDeveloperActionOptions: SelectData;
  cardErrors: ServiceError[];
  saveErrors: ServiceError[];
  isSaving: boolean;
  /** Once Save has been clicked the details step is closed off, however that save turned out. */
  isPreviousDisabled: boolean;
  showAssignResponsibleOwnerModal: boolean;
  handleSelectionChange: (value: AddOrganisationSelection) => void;
  handleOrganisationSearch: (query: string) => void;
  handleAdd: () => void;
  handleRemoveOrganisation: (projectKey: string, organisationUuid: string) => void;
  handleResponsibleOwnerChange: (projectKey: string, organisationUuid: string) => void;
  handleConfirmResponsibleOwner: () => void;
  handleCancelResponsibleOwner: () => void;
  handlePrevious: () => void;
  handleSave: () => void;
}

export const useProjectAccessStep = (): UseProjectAccessStepReturnData => {
  const {
    state,
    updateDetails,
    updateAccess,
    setCreatedProjects,
    markSaveAttempted,
    goToStep,
    creatingOrganisation,
    variant,
    corners,
    onComplete,
  } = useNewProjectJourneyContext();

  const { details, access, createdProjects } = state;
  const creatingOrganisationUuid = creatingOrganisation.uuid;

  const [selection, setSelection] = useState<AddOrganisationSelection>(emptySelection);
  const [cardErrors, setCardErrors] = useState<ServiceError[]>([]);
  const [saveErrors, setSaveErrors] = useState<ServiceError[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingResponsibleOwner, setPendingResponsibleOwner] = useState<{
    projectKey: string;
    organisationUuid: string;
  } | null>(null);
  // Remounts the rollup so a cancelled responsible-owner change cannot leave the select showing a
  // value the journey state never took.
  const [rollupResetKey, setRollupResetKey] = useState(0);
  const isSavingRef = useRef(false);

  const organisationSearch = useOrganisationSearch({ excludeOrganisationUuid: creatingOrganisationUuid });

  const rows = useMemo(() => getActiveProjectRows(details), [details]);

  const projectOptions = useMemo(
    () => rows.map((row, index) => ({ key: row.key, value: projectDisplayName(row, index) })),
    [rows]
  );

  const projectNameByKey = useMemo(
    () => Object.fromEntries(rows.map((row, index) => [row.key, projectDisplayName(row, index)])),
    [rows]
  );

  // A Developer/Full-access add onto a project whose access list already holds a full-access
  // Developer is a swap, not a second developer - a project can only have one (BR-02726/BR-02878),
  // so the add has to say what becomes of the one it displaces (BR-02873), the same question the
  // project's own Access tab invite form asks. Which organisation holds the role does not matter -
  // the creating organisation's own Full-access entry (REQ-003) and another organisation's staged
  // add both get asked about; applyExistingDeveloperActions is where that distinction is made.
  const existingDeveloperPrompts = useMemo((): ExistingDeveloperPromptOption[] => {
    if (selection.access !== OrganisationTypeConstants.DEVELOPER) return [];

    return getProjectsNeedingExistingDeveloperAction(
      selection.projectKeys,
      access.organisations,
      selection.organisationUuid
    ).map((prompt) => ({ ...prompt, projectName: projectNameByKey[prompt.projectKey] ?? "" }));
  }, [selection.access, selection.projectKeys, selection.organisationUuid, access.organisations, projectNameByKey]);

  // The creating organisation is on every project it creates (REQ-003) - kept as a real
  // access.organisations entry, reconciled here whenever the rows or another organisation's own
  // existingDeveloperAction changes, rather than mutated once at Add time (see syncCreatingOrganisation).
  useEffect(() => {
    updateAccess((prev) => {
      const organisations = syncCreatingOrganisation(prev.organisations, rows, creatingOrganisation);
      return organisations === prev.organisations ? prev : { ...prev, organisations };
    });
  }, [creatingOrganisation, rows, access.organisations, updateAccess]);

  const handleSelectionChange = useCallback(
    (value: AddOrganisationSelection) => {
      setCardErrors([]);

      if (value.organisationType !== selection.organisationType) {
        // The card already clears the organisation and access when the type changes; the list they
        // were chosen from has to follow.
        organisationSearch.search(value.organisationType, "");
      }

      setSelection(value);
    },
    [organisationSearch, selection.organisationType]
  );

  const handleOrganisationSearch = useCallback(
    (query: string) => organisationSearch.search(selection.organisationType, query),
    [organisationSearch, selection.organisationType]
  );

  const handleAdd = useCallback(() => {
    const errors = validateAddSelection(selection, access.organisations, existingDeveloperPrompts, projectNameByKey);

    if (errors.length > 0) {
      setCardErrors(errors);
      return;
    }

    const organisationUuid = selection.organisationUuid as string;
    const role = selection.access as string;
    const directoryEntry = organisationSearch.lookup(organisationUuid);
    const accessLabel =
      ProjectAccessConstants.getProjectBuilderAccessOptions(selection.organisationType).find(
        (option) => option.key === role
      )?.value ?? role;

    // The answers are applied before the add lands: a displaced staged add has to stop being this
    // project's Developer assignment for the incoming one to be its only one, and only a swap of
    // the creating organisation's own access is left for the api to perform.
    updateAccess((prev) => {
      const applied = applyExistingDeveloperActions(
        prev,
        {
          organisationUuid,
          projectKeys: selection.projectKeys,
          existingDeveloperActions: selection.existingDeveloperActions ?? {},
        },
        creatingOrganisationUuid
      );

      return {
        ...prev,
        responsibleOwners: applied.responsibleOwners,
        organisations: [
          ...applied.organisations,
          {
            key: uuidv4(),
            organisationUuid,
            organisationName: directoryEntry?.name ?? "",
            organisationType: directoryEntry?.type ?? selection.organisationType ?? "",
            subOrganisationUuid: directoryEntry?.subOrganisationUuid ?? null,
            role,
            accessLabel: String(accessLabel),
            projectKeys: [...selection.projectKeys],
            existingDeveloperActions: applied.existingDeveloperActions,
          } as AddedOrganisationAssignment,
        ],
      };
    });

    setCardErrors([]);
    setSelection(emptySelection());
    organisationSearch.clear();
  }, [
    access.organisations,
    creatingOrganisationUuid,
    existingDeveloperPrompts,
    organisationSearch,
    projectNameByKey,
    selection,
    updateAccess,
  ]);

  const landholdingNameByRowKey = useMemo(() => getLandholdingNameByRowKey(details), [details]);

  const rollupRows = useMemo(
    () =>
      buildRollupRows(
        rows,
        access.organisations,
        access.responsibleOwners,
        creatingOrganisationUuid,
        landholdingNameByRowKey
      ),
    [rows, access.organisations, access.responsibleOwners, creatingOrganisationUuid, landholdingNameByRowKey]
  );

  const responsibleOwnerOptions = useCallback(
    (projectKey: string) => buildResponsibleOwnerOptions(projectKey, access.organisations),
    [access.organisations]
  );

  // Nothing has been sent yet, so taking an organisation off a project is just dropping what was
  // staged for it - no confirmation, and the rollup is the only place it exists.
  const handleRemoveOrganisation = useCallback(
    (projectKey: string, organisationUuid: string) => {
      updateAccess((prev) => ({
        ...prev,
        ...removeOrganisationFromProject(prev, projectKey, organisationUuid, creatingOrganisationUuid),
      }));
    },
    [creatingOrganisationUuid, updateAccess]
  );

  // REQ-026 - confirm before the designation moves off whoever currently holds it.
  const handleResponsibleOwnerChange = useCallback(
    (projectKey: string, organisationUuid: string) => {
      const current = rollupRows.find((row) => row.projectKey === projectKey)?.responsibleOwnerOrganisationUuid;
      if (current === organisationUuid) return;

      setPendingResponsibleOwner({ projectKey, organisationUuid });
    },
    [rollupRows]
  );

  const handleConfirmResponsibleOwner = useCallback(() => {
    if (!pendingResponsibleOwner) return;

    const { projectKey, organisationUuid } = pendingResponsibleOwner;
    updateAccess((prev) => ({
      ...prev,
      responsibleOwners: { ...prev.responsibleOwners, [projectKey]: organisationUuid },
    }));
    setPendingResponsibleOwner(null);
  }, [pendingResponsibleOwner, updateAccess]);

  const handleCancelResponsibleOwner = useCallback(() => {
    setPendingResponsibleOwner(null);
    setRollupResetKey((previous) => previous + 1);
  }, []);

  // Going back after a partial save would let the rows be replaced, minting new uuids the access
  // list above no longer points at - the corrected projects would be created without the
  // organisations chosen for them. So the details step is closed off once anything is written.
  const isPartiallyCreated = useMemo(() => getIsPartiallyCreated(details, createdProjects), [details, createdProjects]);
  // Wider than isPartiallyCreated: any click of Save closes the details step, even one that wrote
  // nothing or failed outright.
  const isPreviousDisabled = state.isSaveAttempted || isPartiallyCreated;

  const handlePrevious = useCallback(() => {
    if (isPreviousDisabled) return;
    goToStep(NewProjectJourneyStep.DETAILS);
  }, [goToStep, isPreviousDisabled]);

  // The api is what judges the rows, so a create failure is written back onto the details step as
  // well: Previous then shows each rejected row against its own fields, or beside the file it came
  // from, which one error box on this step cannot do for a batch of many projects.
  const reportCreateErrorsToDetailsStep = useCallback(
    (errors: ServiceError[]): void => updateDetails((prev) => ({ ...prev, validationErrors: errors })),
    [updateDetails]
  );

  const handleSave = useCallback(() => {
    if (isSavingRef.current) return;

    isSavingRef.current = true;
    markSaveAttempted();
    setIsSaving(true);
    setSaveErrors([]);

    const stopSaving = (): void => {
      isSavingRef.current = false;
      setIsSaving(false);
    };

    saveNewProjectJourney({ details, access, createdProjects, rows, projectNameByKey, creatingOrganisationUuid })
      .then((result) => {
        if (result.status === "createFailed") {
          // Kept when part of the create went through, so the retry sends only the rest (BR-02888).
          if (result.created) setCreatedProjects(result.created);
          reportCreateErrorsToDetailsStep(result.rawErrors);
          setSaveErrors(result.errors);
          stopSaving();
          return;
        }

        // Kept so a retry after a failed invite reuses what the api already accepted (BR-02888).
        setCreatedProjects(result.created);

        if (result.status === "inviteFailed") {
          setSaveErrors(result.errors);
          stopSaving();
          return;
        }

        // Save stays locked: the step remains on screen until the host moves the user on.
        const isLandholding = details.entryMethod === "manual" && details.isLandholding;
        onComplete({
          ...result.created,
          projectCount: Object.keys(result.created.projectUuidByKey).length,
          invitationCount: result.invitationCount,
          isLandholding,
          rows,
          landholding: isLandholding ? details.data.landholding : null,
        });
      })
      .catch(() => {
        setSaveErrors([
          { code: "UNEXPECTED_ERROR", message: "Something went wrong saving your projects. Please try again." },
        ]);
        stopSaving();
      });
  }, [
    access,
    createdProjects,
    creatingOrganisationUuid,
    details,
    markSaveAttempted,
    onComplete,
    projectNameByKey,
    reportCreateErrorsToDetailsStep,
    rows,
    setCreatedProjects,
  ]);

  return {
    variant,
    corners,
    organisationTypeOptions: NEW_PROJECT_ORGANISATION_TYPE_OPTIONS,
    organisationOptions: organisationSearch.organisationOptions,
    accessOptions: ProjectAccessConstants.getProjectBuilderAccessOptions(selection.organisationType),
    projectOptions,
    selection,
    rollupRows,
    rollupResetKey,
    responsibleOwnerOptions,
    existingDeveloperPrompts,
    existingDeveloperActionOptions: ProjectAccessConstants.EXISTING_DEVELOPER_ACTION_OPTIONS,
    cardErrors,
    saveErrors,
    isSaving,
    isPreviousDisabled,
    showAssignResponsibleOwnerModal: pendingResponsibleOwner !== null,
    handleSelectionChange,
    handleOrganisationSearch,
    handleAdd,
    handleRemoveOrganisation,
    handleResponsibleOwnerChange,
    handleConfirmResponsibleOwner,
    handleCancelResponsibleOwner,
    handlePrevious,
    handleSave,
  };
};
